import { TagOption } from 'js/shared/components/Tags';
import { UseTagsStatePath } from 'js/shared/hooks/useTags';
import {
  RatingOption,
  ExistingScribeModel,
  AddAIGenerationTrigger,
  AiPanelTab,
  AiPanelStep,
  ImageGenerationInputs,
  ScriptGenerationInputs
} from 'js/types';

export const CHANGE_AI_PANEL_STEP = 'CHANGE_AI_PANEL_STEP';
export const GENERATE_IMAGES = 'GENERATE_IMAGES';
export const REGENERATE_IMAGES = 'REGENERATE_IMAGES';
export const RESET_IMAGE_GENERATION = 'RESET_IMAGE_GENERATION';
export const IMAGE_GENERATION_SUCCESS = 'IMAGE_GENERATION_SUCCESS';
export const IMAGE_GENERATION_FAILED = 'IMAGE_GENERATION_FAILED';
export const SUBMIT_IMAGE_FEEDBACK = 'SUBMIT_IMAGE_FEEDBACK';
export const SET_IMAGE_GENERATION_INPUTS = 'SET_IMAGE_GENERATION_INPUTS';
export const ADD_GENERATED_IMAGE_TO_PROJECT = 'ADD_GENERATED_IMAGE_TO_PROJECT';
export const RATE_IMAGE_GENERATION_RESULT = 'RATE_IMAGE_GENERATION_RESULT';
export const TRY_NEW_IMAGE_PROMPT = 'TRY_NEW_IMAGE_PROMPT';
export const SET_SCRIPT_GENERATION_INPUTS = 'SET_SCRIPT_GENERATION_INPUTS';
export const GENERATE_SCRIPT = 'GENERATE_SCRIPT';
export const GENERATE_SCRIPT_FAILED = 'GENERATE_SCRIPT_FAILED';
export const GENERATE_SCRIPT_SUCCESS = 'GENERATE_SCRIPT_SUCCESS';
export const SUBMIT_SCRIPT_FEEDBACK = 'SUBMIT_SCRIPT_FEEDBACK';
export const RESET_SCRIPT_GENERATION = 'RESET_SCRIPT_GENERATION';
export const EDIT_GENERATED_SCRIPT = 'EDIT_GENERATED_SCRIPT';
export const SET_GENERATION_FEEDBACK = 'SET_GENERATION_FEEDBACK';
export const RATE_SCRIPT_GENERATION_RESULT = 'RATE_SCRIPT_GENERATION_RESULT';
export const SAVE_GENERATED_SCRIPT = 'SAVE_GENERATED_SCRIPT';
export const TRY_NEW_SCRIPT_PROMPT = 'TRY_NEW_SCRIPT_PROMPT';
export const SET_TAGS = 'SET_TAGS';
export const SET_FEEDBACK_COMMENTS = 'SET_FEEDBACK_COMMENTS';
export const EDIT_IMAGE_PROMPT = 'EDIT_IMAGE_PROMPT';

export enum ChangeStepTrigger {
  LEFT_HAND_PANEL = 'Left Hand Panel',
  IMAGE_LIBRARY = 'Image Library',
  LEFT_HAND_PANEL_TAB_HEADING = 'Left Hand Panel Tab Heading',
  AUDIO_LEFT_HAND_PANEL = 'Audio Left Hand Panel',
  AUDIO_LIBRARY = 'Audio Library',
  PROJECT_SCRIPT_DRAWER = 'Project Script Drawer',
  SCRIPT_GENERATION_TAB = 'Script Generation Tab',
  AI_OVERVIEW = 'AI Overview',
  VOICOVER_DRAWER = 'Voiceover Drawer'
}

export interface ChangeAiPanelStepAction {
  type: typeof CHANGE_AI_PANEL_STEP;
  tab: AiPanelTab;
  step: AiPanelStep;
  trigger?: ChangeStepTrigger;
}

export const changeAiPanelStep = (tab: AiPanelTab, step: AiPanelStep, trigger?: ChangeStepTrigger) => ({
  type: CHANGE_AI_PANEL_STEP,
  tab,
  step,
  trigger
});

export interface GenerateImagesAction {
  type: typeof GENERATE_IMAGES;
}

export const generateImages = (): GenerateImagesAction => ({
  type: GENERATE_IMAGES
});

export interface RegenerateImagesAction {
  type: typeof REGENERATE_IMAGES;
}

export const regenerateImages = (): RegenerateImagesAction => ({
  type: REGENERATE_IMAGES
});

export interface ResetImageGenerationAction {
  type: typeof RESET_IMAGE_GENERATION;
}

export const resetImageGeneration = (): ResetImageGenerationAction => ({
  type: RESET_IMAGE_GENERATION
});

export interface TryNewImagePromptAction {
  type: typeof TRY_NEW_IMAGE_PROMPT;
}

export const tryNewImagePrompt = (): TryNewImagePromptAction => ({
  type: TRY_NEW_IMAGE_PROMPT
});

export interface EditImagePromptAction {
  type: typeof EDIT_IMAGE_PROMPT;
}

export const editImagePrompt = (): EditImagePromptAction => ({
  type: EDIT_IMAGE_PROMPT
});

export interface ImageGenerationSuccessAction {
  type: typeof IMAGE_GENERATION_SUCCESS;
  images: {
    url: string;
    assetId: number;
  }[];
  imageGenerationId: string;
  enhancedPrompt: string;
}

export const imageGenerationSuccess = (
  images: {
    url: string;
    assetId: number;
  }[],
  imageGenerationId: string,
  enhancedPrompt: string
): ImageGenerationSuccessAction => ({
  type: IMAGE_GENERATION_SUCCESS,
  images,
  imageGenerationId,
  enhancedPrompt
});

export interface ImageGenerationFailedAction {
  type: typeof IMAGE_GENERATION_FAILED;
  error: string;
}

export const imageGenerationFailed = (error: string): ImageGenerationFailedAction => ({
  type: IMAGE_GENERATION_FAILED,
  error
});

type ImageFeedbackData = {
  imageId: string;
  rating: RatingOption;
  options: string[];
  comment: string;
};

export interface SubmitImageFeedbackAction {
  type: typeof SUBMIT_IMAGE_FEEDBACK;
  feedbackData: ImageFeedbackData;
}

export const submitImageFeedback = (feedbackData: ImageFeedbackData): SubmitImageFeedbackAction => ({
  type: SUBMIT_IMAGE_FEEDBACK,
  feedbackData
});

export interface SetImageGenerationInputsAction extends Partial<ImageGenerationInputs> {
  type: typeof SET_IMAGE_GENERATION_INPUTS;
}

export const setImageGenerationInputs = (inputs: Partial<ImageGenerationInputs>): SetImageGenerationInputsAction => ({
  type: SET_IMAGE_GENERATION_INPUTS,
  ...inputs
});

interface AddGeneratedImageToProjectInputs {
  assetId: number;
  project: ExistingScribeModel;
  rating?: RatingOption;
  eventTrigger: AddAIGenerationTrigger;
}

export interface AddGeneratedImageToProjectAction {
  type: typeof ADD_GENERATED_IMAGE_TO_PROJECT;
  assetId: number;
  project: ExistingScribeModel;
  rating?: RatingOption;
  eventTrigger: AddAIGenerationTrigger;
}

export const addGeneratedImageToProject = ({
  assetId,
  project,
  rating,
  eventTrigger
}: AddGeneratedImageToProjectInputs): AddGeneratedImageToProjectAction => ({
  type: ADD_GENERATED_IMAGE_TO_PROJECT,
  assetId,
  project,
  rating,
  eventTrigger
});

export interface RateImageGenerationResultAction {
  type: typeof RATE_IMAGE_GENERATION_RESULT;
  rating: RatingOption;
  imageId: string;
}

export const rateImageGenerationResult = (rating: RatingOption, imageId: string): RateImageGenerationResultAction => ({
  type: RATE_IMAGE_GENERATION_RESULT,
  rating,
  imageId
});

export interface SetScriptGenerationInputsAction extends Partial<ScriptGenerationInputs> {
  type: typeof SET_SCRIPT_GENERATION_INPUTS;
}

export const setScriptGenerationInputs = (
  inputs: Partial<ScriptGenerationInputs>
): SetScriptGenerationInputsAction => ({
  type: SET_SCRIPT_GENERATION_INPUTS,
  ...inputs
});

export interface GenerateScriptAction {
  type: typeof GENERATE_SCRIPT;
  projectId?: number;
}

export const generateScript = ({ projectId }: { projectId?: number }): GenerateScriptAction => ({
  type: GENERATE_SCRIPT,
  projectId
});

export interface GenerateScriptFailedAction {
  type: typeof GENERATE_SCRIPT_FAILED;
  error: Error;
}

export const generateScriptFailed = (error: Error): GenerateScriptFailedAction => ({
  type: GENERATE_SCRIPT_FAILED,
  error
});

export interface GenerateScriptSuccessAction {
  type: typeof GENERATE_SCRIPT_SUCCESS;
  script: string;
  projectId?: number;
}

export const generateScriptSuccess = (script: string): GenerateScriptSuccessAction => ({
  type: GENERATE_SCRIPT_SUCCESS,
  script
});

export interface SubmitScriptFeedbackAction {
  type: typeof SUBMIT_SCRIPT_FEEDBACK;
  rating: RatingOption;
  tags: string[];
  comment: string;
}

export const submitScriptFeedback = (
  rating: RatingOption,
  tags: string[],
  comment: string
): SubmitScriptFeedbackAction => ({
  type: SUBMIT_SCRIPT_FEEDBACK,
  rating,
  tags,
  comment
});

export interface EditGeneratedScriptAction {
  type: typeof EDIT_GENERATED_SCRIPT;
  script: string;
}

export const editGeneratedScript = (script: string): EditGeneratedScriptAction => ({
  type: EDIT_GENERATED_SCRIPT,
  script
});

export interface ResetScriptGenerationAction {
  type: typeof RESET_SCRIPT_GENERATION;
}

export const resetScriptGeneration = (): ResetScriptGenerationAction => ({
  type: RESET_SCRIPT_GENERATION
});

export type FeedbackStateUpdatePath = 'script';
export interface SetGenerationFeedbackAction {
  type: typeof SET_GENERATION_FEEDBACK;
  feedback: RatingOption | undefined;
  updatePath: FeedbackStateUpdatePath;
}

export const setGenerationFeedback = (
  feedback: RatingOption | undefined,
  updatePath: FeedbackStateUpdatePath
): SetGenerationFeedbackAction => ({
  type: SET_GENERATION_FEEDBACK,
  feedback,
  updatePath
});

export interface RateScriptGenerationResultAction {
  type: typeof RATE_SCRIPT_GENERATION_RESULT;
  rating: RatingOption;
}

export const rateScriptGenerationResult = (rating: RatingOption): RateScriptGenerationResultAction => ({
  type: RATE_SCRIPT_GENERATION_RESULT,
  rating
});

export interface SaveGeneratedScriptAction {
  type: typeof SAVE_GENERATED_SCRIPT;
  projectId: number;
  script: string;
}

export const saveGeneratedScript = (projectId: number, script: string): SaveGeneratedScriptAction => ({
  type: SAVE_GENERATED_SCRIPT,
  script,
  projectId
});

export interface TryNewScriptPromptAction {
  type: typeof TRY_NEW_SCRIPT_PROMPT;
  reset: boolean;
}

export const tryNewScriptPrompt = (reset: boolean): TryNewScriptPromptAction => ({
  type: TRY_NEW_SCRIPT_PROMPT,
  reset
});

export interface SetTagsAction {
  type: typeof SET_TAGS;
  tags: TagOption[];
  statePath: UseTagsStatePath;
}

export const setTags = (tags: TagOption[], statePath: UseTagsStatePath): SetTagsAction => ({
  type: SET_TAGS,
  tags,
  statePath
});

export interface SetFeedbackCommentsAction {
  type: typeof SET_FEEDBACK_COMMENTS;
  comments: string;
  updatePath: FeedbackStateUpdatePath;
}

export const setFeedbackComments = (
  comments: string,
  updatePath: FeedbackStateUpdatePath
): SetFeedbackCommentsAction => ({
  type: SET_FEEDBACK_COMMENTS,
  comments,
  updatePath
});
